import { formatBRL, type MediaAsset, type Product } from '../api.js';

/**
 * Prévia de como a publicação vai aparecer em cada rede.
 *
 * Não é um mockup pixel-perfect do app — é uma referência de enquadramento e de
 * onde o texto corta. O que importa aqui: ver se a arte funciona na proporção
 * certa, se a primeira linha da legenda entrega o gancho, e (no Stories) onde o
 * sticker de link vai ficar, para você não desenhar a arte por baixo dele.
 */

export type StickerPosition = 'top' | 'middle' | 'bottom';

export type PreviewProps = {
  platform: 'INSTAGRAM' | 'TIKTOK' | 'WHATSAPP';
  format?: 'REELS' | 'STORY' | 'FEED';
  product: Product;
  media: MediaAsset | null;
  caption: string;
  affiliateLink: string | null;
  sticker?: { label: string; position: StickerPosition } | null;
  accountName: string;
};

/** Proporção do enquadramento por formato. */
const RATIO: Record<string, string> = {
  STORY: '9 / 16',
  REELS: '9 / 16',
  TIKTOK: '9 / 16',
  FEED: '4 / 5',
  WHATSAPP: '1 / 1',
};

function MediaBox({
  media,
  ratio,
  children,
}: {
  media: MediaAsset | null;
  ratio: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="pv-media" style={{ aspectRatio: ratio }}>
      {media ? (
        media.kind === 'VIDEO' ? (
          <video src={media.publicUrl} muted loop autoPlay playsInline />
        ) : (
          <img src={media.publicUrl} alt="" />
        )
      ) : (
        <div className="pv-empty">
          <span>sem mídia</span>
          <small>envie a imagem ou vídeo</small>
        </div>
      )}
      {children}
    </div>
  );
}

export function Preview(props: PreviewProps) {
  const { platform, format = 'FEED', product, media, caption, affiliateLink } = props;

  if (platform === 'INSTAGRAM' && format === 'STORY') {
    return <StoryPreview {...props} />;
  }
  if (platform === 'INSTAGRAM') {
    return <InstagramFeedPreview {...props} format={format} />;
  }
  if (platform === 'TIKTOK') {
    return <TiktokPreview {...props} />;
  }
  return (
    <WhatsappPreview
      product={product}
      media={media}
      caption={caption}
      affiliateLink={affiliateLink}
    />
  );
}

function StoryPreview({ media, caption, sticker, accountName }: PreviewProps) {
  const pos = sticker?.position ?? 'bottom';
  return (
    <div className="pv-frame pv-story">
      <MediaBox media={media} ratio={RATIO.STORY!}>
        <div className="pv-story-top">
          <span className="pv-avatar" />
          <span className="pv-handle">{accountName}</span>
          <span className="pv-story-bar" />
        </div>

        {/* O sticker é a razão de existir este preview: a arte não pode ter
            informação importante debaixo dele. */}
        {sticker && (
          <div className={`pv-sticker pv-sticker-${pos}`}>
            <span className="pv-sticker-icon">🔗</span>
            {sticker.label || 'VER OFERTA'}
          </div>
        )}

        {caption.trim() && <div className="pv-story-caption">{caption.trim().slice(0, 90)}</div>}
      </MediaBox>
    </div>
  );
}

function InstagramFeedPreview({ media, caption, format, accountName }: PreviewProps) {
  const ratio = format === 'REELS' ? RATIO.REELS! : RATIO.FEED!;
  const linhas = caption.trim().split('\n');
  const primeira = linhas[0] ?? '';
  const resto = linhas.slice(1).join('\n');

  return (
    <div className="pv-frame">
      <div className="pv-head">
        <span className="pv-avatar" />
        <span className="pv-handle">{accountName}</span>
      </div>
      <MediaBox media={media} ratio={ratio} />
      <div className="pv-actions">♡ ♢ ✈︎</div>
      <div className="pv-caption">
        <strong>{accountName}</strong>{' '}
        {primeira || <span className="pv-placeholder">seu texto aparece aqui</span>}
        {/* O Instagram corta a legenda no "mais": a primeira linha é o que
            realmente decide se a pessoa para de rolar. */}
        {resto && <span className="pv-more"> … mais</span>}
      </div>
    </div>
  );
}

function TiktokPreview({ media, caption, accountName }: PreviewProps) {
  return (
    <div className="pv-frame pv-tiktok">
      <MediaBox media={media} ratio={RATIO.TIKTOK!}>
        <div className="pv-tt-side">
          <span>♡</span>
          <span>💬</span>
          <span>↗</span>
        </div>
        <div className="pv-tt-bottom">
          <strong>@{accountName}</strong>
          <div className="pv-tt-caption">
            {caption.trim().slice(0, 150) || (
              <span className="pv-placeholder">seu texto aparece aqui</span>
            )}
          </div>
        </div>
      </MediaBox>
    </div>
  );
}

function WhatsappPreview({
  product,
  media,
  caption,
  affiliateLink,
}: {
  product: Product;
  media: MediaAsset | null;
  caption: string;
  affiliateLink: string | null;
}) {
  return (
    <div className="pv-frame pv-whats">
      <div className="pv-wa-bubble">
        {media && (
          <div className="pv-wa-media">
            {media.kind === 'VIDEO' ? (
              <video src={media.publicUrl} muted loop autoPlay playsInline />
            ) : (
              <img src={media.publicUrl} alt="" />
            )}
          </div>
        )}
        <div className="pv-wa-text">
          {caption.trim() || <span className="pv-placeholder">seu texto aparece aqui</span>}
        </div>
        {/* O WhatsApp gera um card de prévia do link — vale ver como fica. */}
        <div className="pv-wa-link">
          <span className="pv-wa-link-title">{product.name}</span>
          <span className="pv-wa-link-url">
            {affiliateLink ?? 'o link de afiliado entra aqui'}
          </span>
          {product.kind === 'PRODUCT' && (
            <span className="pv-wa-link-price">{formatBRL(product.priceCents)}</span>
          )}
        </div>
        <span className="pv-wa-time">agora</span>
      </div>
    </div>
  );
}
